import {Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Emali21, LicensedAgent, Phone21} from "../../../shared/interface/licensed-agent";
import {of, ReplaySubject} from "rxjs";
import {Profile} from "../../../shared/interface/profile";
import {Partner} from "../../../shared/interface/partner";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {Ofwca} from "../../../shared/interface/ofwca";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {LicensedAgentService} from "../../../shared/services/licensed-agent.service";
import {PartnerService} from "../../../shared/services/partner.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {AuthService} from "../../../shared/services/auth.service";
import {DictService} from "../../../shared/services/dict.service";
import {validNip} from "../../../shared/validator/nip-validator";
import {ArrayValidators} from "../../../shared/validator/array-validators";
import {delay, takeUntil, tap} from "rxjs/operators";
import {validPhoneNumber} from "../../../shared/validator/phone-validator";
import {HttpErrorResponse} from "@angular/common/http";
import {ListResponse} from "../../../shared/interface/response/list-response";
import * as uuid from 'uuid';
import {
  AGENT_21_STATUS_ACTIVE,
} from "../../../shared/services/dict.service";


@Component({
  selector: 'app-licensed-details-form',
  templateUrl: './licensed-details-form.component.html',
  styleUrls: ['./licensed-details-form.component.scss']
})
export class LicensedDetailsFormComponent implements OnInit, OnDestroy  {
  @Input() licensedObject?: LicensedAgent;
  @Output() closeModification = new EventEmitter<any>();


  items: LicensedAgent[] = [];
  profile?: Profile;
  addMode = false;
  partnerObject: Partner;
  licensedAgentForm: FormGroup;
  expirationAtControl: FormControl;
  invoiceNipControl: FormControl;
  licenseNipControl: FormControl;
  licensePayerControl: FormControl;
  partnerControl: FormControl;
  ofwcaControl: FormControl;
  phones: FormGroup[] = [];
  emails: FormGroup[] = [];
  partnerOfwca: Ofwca[] = [];
  errors: any = {};
  ofwca = false;
  userPermissions: ReplaySubject<UserPermissions>;

  readonly AGENT_21_STATUS_ACTIVE = AGENT_21_STATUS_ACTIVE;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private initialized: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private licensedAgentService: LicensedAgentService,
    private partnerService: PartnerService,
    private fb: FormBuilder,
    private snack: MatSnackBar,
    private auth: AuthService,
    private dictService: DictService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.partnerControl = fb.control('', [Validators.required]);
    this.licensedAgentForm = fb.group({
      invoiceNip: this.invoiceNipControl = fb.control('', [Validators.required, validNip()]),
      licenseNip: this.licenseNipControl = fb.control('', [Validators.required, validNip()]),
      phones: fb.array([], [ArrayValidators.minLength(1)]),
      emails: fb.array([], [ArrayValidators.minLength(1)]),
      licensePayer: this.licensePayerControl = fb.control('', [Validators.required]),
      partnerShortName: fb.control('', [Validators.required]),
      partnerId: fb.control('', [Validators.required]),
      ofwca: this.ofwcaControl = fb.control('', [Validators.required]),
      expirationAt: this.expirationAtControl =  fb.control('', []),

    });
  }

  ngOnInit(): void {
    this.auth.getProfile()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((profile: Profile) => {
        this.profile = profile;
      });

    this.licensePayerControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((dictId: number) => {
        this.dictService.getKey(dictId)
          .subscribe((dictKey: string) => {
            if (dictKey === 'ofwca') {
              this.ofwcaControl.setValidators([Validators.required]);
              this.ofwcaControl.enable();
              this.ofwca = true;
            } else  if (dictKey === 'partner') {
              this.ofwcaControl.setValidators([]);
              this.ofwcaControl.reset();
              this.ofwcaControl.disable();
              this.ofwca = false;
            }
          });
      });
    this.partnerControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((id: any) => {
        this.partnerService.get(id)
          .subscribe((partner: Partner) => {
            this.getOfwca(id);
            this.licensedAgentForm.get('partnerShortName').setValue(partner.shortName);
            this.licensedAgentForm.get('partnerId').setValue(partner.recordId);
          });
      });
    this.initialize(this.licensedObject);

  }

  initialize(licensedAgent: LicensedAgent): void {
    of(licensedAgent)
      .pipe(
        delay(0),
        tap(() => {
          this.partnerObject = licensedAgent.partner;
          this.partnerControl.setValue(licensedAgent.partner?.uuid);
          this.licensedAgentForm.reset({
            invoiceNip: licensedAgent.invoiceNip,
            licenseNip: licensedAgent.licenseNip,
            licensePayer: licensedAgent.payer?.id,
            partnerShortName: licensedAgent.partner?.shortName,
            partnerId:  licensedAgent.partner?.recordId,
            ofwca:  licensedAgent.ofwca?.uuid,
            expirationAt: licensedAgent.expirationAt,
          });
          licensedAgent.emails.forEach((email: Emali21) => this.addEmail('emails', email.uuid, email.email, email.name));

          licensedAgent.phones.forEach((phone: Phone21) => this.addPhone('phones', phone.uuid, phone.phone, phone.name));
        }),
        delay(0),
        tap(() => {
          this.initialized.next(true);
        })
      ).subscribe();
  }

  addPhone(type: string, recordUuid?: string, phone?: string, name?: string): void {
    this[type].push(this.fb.group({
      uuid: this.fb.control(recordUuid ?? uuid.v4(), [Validators.required]),
      phone: this.fb.control(phone ?? '', [Validators.required, validPhoneNumber()]),
      name: this.fb.control(name ?? ''),
    }));
    this.refreshPhoneControl(type);
  }
  addEmail(type: string, recordUuid?: string, email?: string, name?: string): void {
    this[type].push(this.fb.group({
      uuid: this.fb.control(recordUuid ?? uuid.v4(), [Validators.required]),
      email: this.fb.control(email ?? '', [Validators.required, Validators.email]),
      name: this.fb.control(name ?? ''),
    }));
    this.refreshEmailControl(type);
  }
  closeDialog(e): void {
    this.closeModification.emit();
  }

  removePhone(i: number, type: string): void {
    this[type].splice(i, 1);
    this.refreshPhoneControl(type);
  }
  refreshPhoneControl(type: string): void {
    const validators = type === 'phones' ? [ArrayValidators.minLength(1)] : [];
    this.licensedAgentForm.setControl(type, this.fb.array(this[type], validators));
  }
  refreshEmailControl(type: string): void {
    const validators = type === 'emails' ? [ArrayValidators.minLength(1)] : [];
    this.licensedAgentForm.setControl(type, this.fb.array(this[type], validators));
  }
  removeEmail(i: number, type: string): void {
    this[type].splice(i, 1);
    this.refreshEmailControl(type);
  }

  // downloadPartner(): void{
  //   this.invoiceNipControl.reset();
  //   this.licenseNipControl.reset();
  //   this.licensePayerControl.reset();
  //   this.licensedAgentForm.get('phones').reset();
  //   this.licensedAgentForm.get('emails').reset();
  //   this.licensedAgentForm.get('licenseNip').setValue(this.partnerObject.nip);
  //   this.licensedAgentForm.get('invoiceNip').setValue(this.partnerObject.nip);
  //   this.partnerObject.phones.filter((phone: PhoneNumber) => phone.type.key === 'primary')
  //     .forEach((phone: PhoneNumber) => this.addPhone('phones', phone.uuid, phone.number, phone.name));
  //   this.partnerObject.emails.filter((email: EmailAddress)  => email.type.key === 'primary')
  //     .forEach((email: EmailAddress) => this.addEmail('emails', email.uuid, email.address, email.name));
  //
  // }
  sendForm(): void {
    if (!this.licensedAgentForm.valid) {
      this.licensedAgentForm.markAllAsTouched();
      this.snack.open('Wypełnij lub popraw wszystkie wymagane pola', '', {
        duration: 2000,
      });
      return;
    }
    this.licensedAgentService.send(this.licensedAgentForm.getRawValue(), this.licensedObject.uuid)
      .subscribe(() => {
        this.snack.open('Pomyślnie zaktualizowano licencję ', '', {duration: 2000});
        this.addMode = false;
        this.closeModification.emit();
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy aktualizacji zgłoszenia wystąpił błąd', error.status.toString(), {duration: 2000});
        if (error.status === 400) {
          this.snack.open(error.error.error.text, '400', {duration: 4000});
        }
      });
  }

  getOfwca(uuid: string): void{
    this.partnerOfwca =  [];
    this.partnerService.getPartnerOfwca(uuid)
      .subscribe((Ofwcas: ListResponse<Ofwca>) => {
        if (Ofwcas.total > 0) {
          Ofwcas.items.forEach((ofwca: Ofwca) => {
            this.partnerOfwca.push(ofwca);
          });
        }
      });
    if (this.licensedObject.ofwca) {
      this.partnerOfwca.push(this.licensedObject.ofwca);
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }


}
